import {
  type Alias,
  type DeltaTarget,
  type DeltaTiming,
  type Label,
  labelAt,
  type Operation,
  type ProposedDelta,
  resolveAlias,
  type ValidationResult,
} from "@pax/world";
import {
  applyDelta,
  copyWorld,
  type PerspectiveState,
  type ViewState,
  type WorldState,
} from "./world-state";

/** A delta the game master proposes, paced by `timing` when given. */
export function gameMasterDelta(
  target: DeltaTarget,
  operation: Operation,
  timing?: DeltaTiming,
): ProposedDelta {
  return { actor: { type: "gameMaster" }, target, ...(timing ? { timing } : {}), operation };
}

/** The state as it would be once a batch commits, without touching the one given. */
export function stateAfter(state: WorldState, batch: readonly ProposedDelta[]): WorldState {
  const next = copyWorld(state);
  const committedAt = new Date().toISOString();
  for (const [index, delta] of batch.entries()) {
    applyDelta(next, {
      ...delta,
      actor: { type: "gameMaster" },
      revision: state.revision + index + 1,
      committedAt,
    });
  }
  return next;
}

/** The spectator and every perspective with users: where the world's round deltas go. */
export const ACTIVE_VIEWS: DeltaTarget = { type: "activeViews" };

/** The spectator and every perspective: the target for anything every view sees. */
export function everyView(state: WorldState): DeltaTarget {
  return {
    type: "perspectives",
    perspectives: [
      { type: "spectator" },
      ...Object.keys(state.perspectives)
        .sort()
        .map(id => ({ type: "perspective" as const, handle: id })),
    ],
  };
}

/**
 * The deltas that bring a perspective without users in sync with the spectator once `before`
 * commits, so a user can join it: its clock started at the spectator's latest round boundary, or
 * its jumps backfilled up to it. None when it has users, when `before` does not validate, or while
 * a jump is active.
 */
export function catchUpDeltas(
  game: { validate(deltas: readonly ProposedDelta[]): ValidationResult },
  perspectiveHandle: string,
  before: readonly ProposedDelta[] = [],
): ProposedDelta[] {
  const after = game.validate(before);
  if (!after.ok) return [];
  const view = after.indexes.perspectives.get(perspectiveHandle);
  const spectator = after.indexes.spectator.clock;
  if (!view || (view.perspective?.userIds.size ?? 0) > 0 || !spectator || spectator.jumping) {
    return [];
  }
  const target = perspectiveTarget(perspectiveHandle);
  const latest = { roundCompleted: spectator.round - 1, tick: spectator.tick };
  const { clock } = view;
  if (!clock) return [gameMasterDelta(target, { type: "world.started", ...latest })];
  const operations: Operation[] = [];
  let round = clock.round;
  if (clock.jumping) {
    const boundary = after.indexes.wholeRecord.boundaries.find(
      ({ roundCompleted }) => roundCompleted === clock.round,
    );
    if (!boundary) return [];
    operations.push({ type: "jump.completed", ...boundary });
    round += 1;
  }
  if (round < spectator.round) {
    operations.push(
      { type: "jump.started", round: latest.roundCompleted },
      { type: "jump.completed", ...latest },
    );
  }
  return operations.map(operation => gameMasterDelta(target, operation));
}

export function perspectiveTarget(perspectiveHandle: string): DeltaTarget {
  return {
    type: "perspectives",
    perspectives: [{ type: "perspective", handle: perspectiveHandle }],
  };
}

/** Perspectives someone is playing. A jump waits for each of them to end its turn. */
export function playedPerspectives(state: WorldState): PerspectiveState[] {
  return Object.values(state.perspectives)
    .filter(view => view.userIds.length > 0)
    .sort((left, right) => left.perspective.handle.localeCompare(right.perspective.handle));
}

/** How a view sees a perspective, by its handle: its alias there. */
export function aliasIn(view: ViewState, handle: string): Alias {
  return resolveAlias(view.aliases[handle]);
}

/** What a perspective calls itself: its alias in its own view. */
export function ownAlias(view: PerspectiveState): Alias {
  return aliasIn(view, view.perspective.handle);
}

/** The date the current round began on: the spectator's label where it began, or null where it has none. */
export function roundDate(state: WorldState): string | null {
  return labelAt(state.spectator.timeline.labels, state.roundTick);
}

/** Labels in words, each with its tick: "tick 7, May 18, 1846; tick 11, May 22, 1846". */
export function labelsInWords(labels: readonly Label[] | undefined): string {
  return (labels ?? []).map(label => `tick ${label.tick}, ${label.date}`).join("; ");
}
